import { RiArrowDownSLine, RiArrowUpSLine, RiDeleteBinLine } from '@remixicon/react';
import { Button, Footnote, FormGroup, Group, Row, StatusPill } from '@store/ui';
import { haptic } from '../../telegram';
import { isTypedAmount } from './money';
import { ICON_BUTTON, ICON_BUTTON_DANGER, TextRow, ToggleRow } from './fields';

/**
 * Варианты.
 *
 * A product with no variants is a supported and common case — `/checkout/quote`
 * prices the product itself when `variantId` is null, and most of this shop is
 * one thing at one price. So this section opens empty and stays empty until the
 * owner asks for an option. Nothing here ever invents a first variant to fill
 * the space: that silently changes what the shop sells, and it changes where the
 * stock has to be loaded, because a variant owns its own pool.
 */

export interface VariantDraft {
  /** Stable across reorders and renames, so React does not recycle two rows. */
  readonly key: string;
  /** Null until the server has been told about it. */
  readonly id: string | null;
  title: string;
  /** Roubles, as typed. Converted once, at save. */
  price: string;
  durationDays: string;
  isPublished: boolean;
  /** What the server says is in this variant's pool. Read-only here. */
  readonly available: number | null;
  /** Marked for removal; kept in the list until the save goes through. */
  removed: boolean;
}

let counter = 0;
export const newVariantKey = (): string => `v${(counter += 1)}`;

export const emptyVariant = (): VariantDraft => ({
  key: newVariantKey(),
  id: null,
  title: '',
  price: '',
  durationDays: '',
  isPublished: true,
  available: null,
  removed: false,
});

export function VariantsSection({
  variants,
  decimals,
  onChange,
}: {
  readonly variants: readonly VariantDraft[];
  readonly decimals: number;
  readonly onChange: (next: VariantDraft[]) => void;
}) {
  const live = variants.filter((variant) => !variant.removed);

  const patch = (key: string, changes: Partial<VariantDraft>) => {
    onChange(
      variants.map((variant) => (variant.key === key ? { ...variant, ...changes } : variant)),
    );
  };

  /**
   * Moves a variant past its neighbour *in the visible order*.
   *
   * Indexed against `live` rather than the whole array: a variant marked for
   * removal is still in the list, and swapping with one of those looks to the
   * owner like the button did nothing.
   */
  const move = (key: string, direction: -1 | 1) => {
    const order = live.map((variant) => variant.key);
    const from = order.indexOf(key);
    const to = from + direction;
    if (from < 0 || to < 0 || to >= order.length) return;

    haptic.select();
    const swapped = [...order];
    swapped[from] = order[to]!;
    swapped[to] = order[from]!;

    const byKey = new Map(variants.map((variant) => [variant.key, variant]));
    const reordered = swapped.map((candidate) => byKey.get(candidate)!);
    onChange([...reordered, ...variants.filter((variant) => variant.removed)]);
  };

  const remove = (key: string) => {
    const target = variants.find((variant) => variant.key === key);
    haptic.impact('light');
    /* A variant the server has never seen is simply dropped. One it has is only
       marked, because the deletion is a request that can fail and a row that
       vanished before it succeeded is a row the owner cannot get back. */
    if (target?.id === null) onChange(variants.filter((variant) => variant.key !== key));
    else patch(key, { removed: true });
  };

  return (
    <div className="stack">
      {live.length === 0 ? (
        <Footnote>
          У этого товара нет вариантов — так и должно быть, если вы продаёте одну позицию по одной
          цене. Варианты нужны, когда у товара разные сроки, регионы или тарифы: у каждого своя цена
          и свой склад.
        </Footnote>
      ) : null}

      {live.map((variant, index) => {
        const priceBad = variant.price.trim() !== '' && !isTypedAmount(variant.price, decimals);

        return (
          <section key={variant.key}>
            <div className="row row--between" style={{ padding: '0 var(--space-4)' }}>
              <p className="section-label" style={{ padding: 0 }}>
                Вариант {index + 1}
              </p>
              <span className="row" style={{ gap: 'var(--space-1)' }}>
                <button
                  type="button"
                  className="iconbutton"
                  style={ICON_BUTTON}
                  aria-label="Выше"
                  disabled={index === 0}
                  onClick={() => move(variant.key, -1)}
                >
                  <RiArrowUpSLine size={18} aria-hidden="true" />
                </button>
                <button
                  type="button"
                  className="iconbutton"
                  style={ICON_BUTTON}
                  aria-label="Ниже"
                  disabled={index === live.length - 1}
                  onClick={() => move(variant.key, 1)}
                >
                  <RiArrowDownSLine size={18} aria-hidden="true" />
                </button>
                <button
                  type="button"
                  className="iconbutton iconbutton--danger"
                  style={ICON_BUTTON_DANGER}
                  aria-label={`Удалить вариант ${variant.title || index + 1}`}
                  onClick={() => remove(variant.key)}
                >
                  <RiDeleteBinLine size={18} aria-hidden="true" />
                </button>
              </span>
            </div>

            <FormGroup>
              <TextRow
                label="Название"
                id={`variant-title-${variant.key}`}
                value={variant.title}
                onChange={(value) => patch(variant.key, { title: value })}
                placeholder="12 месяцев"
                maxLength={160}
                invalid={variant.title.trim() === ''}
              />
              <TextRow
                label="Цена, $"
                id={`variant-price-${variant.key}`}
                value={variant.price}
                onChange={(value) => patch(variant.key, { price: value })}
                placeholder="1990"
                inputMode="decimal"
                invalid={priceBad}
              />
              <TextRow
                label="Срок, дней"
                id={`variant-days-${variant.key}`}
                value={variant.durationDays}
                onChange={(value) => patch(variant.key, { durationDays: value })}
                placeholder="Например, 365"
                inputMode="numeric"
              />
            </FormGroup>

            <Group>
              <ToggleRow
                title="Продаётся"
                subtitle="Снимите, чтобы вариант перестал показываться покупателю"
                value={variant.isPublished}
                onChange={(value) => patch(variant.key, { isPublished: value })}
              />
              <Row
                title="Остаток варианта"
                subtitle={
                  variant.id === null
                    ? 'Появится после сохранения'
                    : 'Загружается на экране «Склад»'
                }
                trailing={
                  variant.available === null ? (
                    <span className="muted">—</span>
                  ) : variant.available === 0 ? (
                    <StatusPill tone="danger">Пусто</StatusPill>
                  ) : (
                    <span className="price">{variant.available}</span>
                  )
                }
              />
            </Group>

            {priceBad ? (
              <Footnote>
                Цена в долларах: только цифры, максимум {decimals} знака после запятой.
              </Footnote>
            ) : null}
          </section>
        );
      })}

      <Button
        variant="secondary"
        block
        onClick={() => {
          haptic.impact('light');
          onChange([...variants, emptyVariant()]);
        }}
      >
        Добавить вариант
      </Button>

      {variants.some((variant) => variant.removed) ? (
        <Footnote>
          Помеченные к удалению варианты исчезнут после сохранения. Пока сохранение не прошло, они
          остаются в магазине.
        </Footnote>
      ) : null}
    </div>
  );
}
